// =====================================================================
// myCRM — Research module (dashboard, surveys, interviews, champions, insights, ICP)
// =====================================================================

function ResearchDashboard({ navigate }) {
  const surveysSent = D.SURVEYS.length;
  const surveysCompleted = D.SURVEYS.filter(s => s.status === "Completed").length;
  const interviewsCompleted = D.INTERVIEWS.filter(i => i.status === "Completed").length;
  const fatigueCount = D.CONTACTS.filter(c => c.fatigue).length;

  return (
    <>
      <PageHead title="Research" sub="Champion · Influencer · ICP fitness · Insights — embedded in every sales record" />
      <div className="grid grid-4 mt-8" style={{ marginTop: 0, marginBottom: 12 }}>
        <Stat label="Surveys sent (90d)" value={surveysSent} delta="+12 vs prior" />
        <Stat label="Survey response rate" value={surveysSent > 0 ? `${Math.round(surveysCompleted/surveysSent*100)}%` : "—"} delta="−3pp" trend="down" />
        <Stat label="Interviews completed (90d)" value={interviewsCompleted} delta="+6" />
        <Stat label="Contacts in fatigue window" value={fatigueCount} delta="protect from outreach" trend="down" />
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head"><div className="card-title">Insight backlog</div><div className="card-sub">Synthesised from all research touchpoints</div></div>
          <div className="card-body">
            {D.INSIGHTS.map(i => (
              <div key={i.id} className="medd-row" onClick={() => navigate("research/insights/" + i.id)} style={{ cursor: "pointer" }}>
                <div className="medd-letter">◆</div>
                <div>
                  <div className="medd-label">{i.title}</div>
                  <div className="medd-desc">{i.theme} · {i.source} · evidence from {i.evidenceCount} touchpoints</div>
                </div>
                <Badge tone={i.confidence === "High" ? "green" : i.confidence === "Medium" ? "amber" : "slate"}>{i.confidence}</Badge>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Pain themes (last 90d)</div><div className="card-sub">across {D.SURVEYS.length + D.INTERVIEWS.length} responses</div></div>
          <div className="card-body">
            {(() => {
              const all = [...D.SURVEYS, ...D.INTERVIEWS].flatMap(r => r.pain || []);
              const counts = {};
              all.forEach(p => counts[p] = (counts[p] || 0) + 1);
              const max = Math.max(...Object.values(counts), 1);
              return Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([k,v]) => (
                <BarRow key={k} label={k} value={v} max={max} />
              ));
            })()}
          </div>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head"><div className="card-title">Top champions</div></div>
          <table className="tbl tbl-flush">
            <thead><tr><th>Contact</th><th>Account</th><th>Strength</th><th>Last validated</th></tr></thead>
            <tbody>{(() => {
              // A "champion" is now per-deal — surface every contact who
              // holds the Champion role on at least one opportunity, taking
              // their highest confidence across deals as the strength.
              const byContact = new Map();
              for (const oc of (D.OPPORTUNITY_CONTACTS || [])) {
                if (oc.role !== "Champion") continue;
                const prev = byContact.get(oc.contactId);
                if (!prev || (oc.roleConfidence || 0) > (prev.roleConfidence || 0)) byContact.set(oc.contactId, oc);
              }
              return Array.from(byContact.values()).slice(0, 6).map(oc => {
                const c = getContact(oc.contactId);
                if (!c) return null;
                const a = getAccount(c.accountId) || {};
                const conf = oc.roleConfidence || 0;
                return (
                  <tr key={c.id} onClick={() => navigate("sales/contacts/" + c.id)}>
                    <td><span className="tbl-link">{c.first} {c.last}</span></td>
                    <td>{a.name}</td>
                    <td>{"●".repeat(conf)}<span className="muted">{"●".repeat(Math.max(0, 5 - conf))}</span></td>
                    <td>{fmtDateShort(c.lastContact)}</td>
                  </tr>
                );
              });
            })()}</tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">ICP fitness pulse</div><div className="card-sub">Tier mix across active pipeline</div></div>
          <div className="card-body">
            {[
              { tier: "Tier 1", count: D.ACCOUNTS.filter(a=>a.icp==="Tier 1").length, tone: "amber" },
              { tier: "Tier 2", count: D.ACCOUNTS.filter(a=>a.icp==="Tier 2").length, tone: "blue" },
              { tier: "Tier 3", count: D.ACCOUNTS.filter(a=>a.icp==="Tier 3").length, tone: "slate" },
              { tier: "Disqualified", count: D.ACCOUNTS.filter(a=>a.icp==="Disqualified").length, tone: "red" },
            ].map(r => (
              <BarRow key={r.tier} label={r.tier} value={r.count} max={D.ACCOUNTS.length} tone={`bar-${r.tone}`} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// Per-survey-type framing so the generated outreach reads as relevant to
// the kind of survey being sent. Falls back to a generic line.
const SURVEY_MESSAGE_BLURB = {
  "ICP Discovery":       "I'm trying to get a clearer picture of where software delivery actually slows teams like yours down",
  "Champion validation": "I'd really value your read on where your team's SDLC is solid and where it still hurts",
  "Influencer Mapping":  "I'm trying to understand how your team ships software day to day",
  "Pain confirmation":   "I want to be sure I've actually understood the pain points in your delivery process, not just guessed at them",
  "Win/loss":            "I'd love your honest take on how the evaluation went, the good and the bad alike",
  "NPS":                 "I'd like a straight read on how we're doing for you",
  "Pulse":               "I wanted to check in on how things have been going on your side",
};

// Respondent-facing subject lines. Framed around the recipient's world —
// their SDLC pains / maturity — never the internal survey-type label, which
// reads as internal/sales jargon to the person receiving it.
const SURVEY_MESSAGE_SUBJECT = {
  "ICP Discovery":       "What slows your team's software delivery down?",
  "Champion validation": "How mature is your team's SDLC, really?",
  "Influencer Mapping":  "How does your team ship software?",
  "Pain confirmation":   "The parts of your SDLC that hurt most",
  "Win/loss":            "How did the evaluation go for you?",
  "NPS":                 "How are we doing?",
  "Pulse":               "How are things going on your side?",
};

// Builds the copy-paste outreach for a survey send. Email and Manual entry
// share the longer email copy (with a subject); LinkedIn gets a shorter,
// subject-less DM. There is no mail backend — the rep pastes this into
// their own email client / LinkedIn to send to the contact.
function buildSurveyMessage(channel, { contact, formUrl, surveyType, senderName }) {
  const first = (contact && contact.first) || "there";
  const blurb = SURVEY_MESSAGE_BLURB[surveyType] || "I'd really value your perspective on how your team works";
  const subject = SURVEY_MESSAGE_SUBJECT[surveyType] || "A few questions about your software delivery";
  if (channel === "LinkedIn") {
    return {
      subject: "",
      body: "Hi " + first + ", " + blurb + ". I've turned it into a survey, and I'm happy to share back what we find. " +
        "It's a proper one, about 12 to 18 minutes: " + formUrl +
        (senderName ? "\n\nReally appreciate it,\n" + senderName : "\n\nReally appreciate it!"),
    };
  }
  return {
    subject: subject,
    body: "Hi " + first + ",\n\n" +
      blurb.charAt(0).toUpperCase() + blurb.slice(1) + ", so I've put together a survey.\n\n" +
      "It takes about 12 to 18 minutes, which I know is a real ask. The questions go deep, and I'm happy to share back what we find:\n" +
      formUrl +
      (senderName ? "\n\nThanks,\n" + senderName : "\n\nThanks!"),
  };
}

function NewSurveyDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const contacts = D.CONTACTS || [];
  const surveyForms = (D.SETTINGS && D.SETTINGS.surveyForms) || {};
  const { user } = useAuth();
  const senderName = (user && user.name) || "";
  const [form, setForm] = useState({ contactId: "", type: "ICP Discovery", channel: "Email", sent: today, status: "Sent" });
  const [persona, setPersona] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const contact = contacts.find(c => c.id === form.contactId);
  const formUrl = persona ? (surveyForms[persona] || "") : "";
  const message = (formUrl && contact)
    ? buildSurveyMessage(form.channel, { contact, formUrl, surveyType: form.type, senderName })
    : null;

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // Picking a contact defaults the persona to that contact's ICP — still
  // overridable for contacts whose ICP has not been set.
  function pickContact(id) {
    set("contactId", id);
    const c = contacts.find(x => x.id === id);
    setPersona((c && c.icp) || "");
    setCopiedKey(null);
  }

  function copy(text, key) {
    if (text && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(k => (k === key ? null : k)), 1500);
    }
  }

  // mailto: draft — mirrors the generated email preview so the draft and
  // the copy-paste text stay identical.
  function mailtoHref() {
    const m = buildSurveyMessage("Email", { contact, formUrl, surveyType: form.type, senderName });
    return "mailto:" + (contact && contact.email ? contact.email : "") +
      "?subject=" + encodeURIComponent(m.subject) + "&body=" + encodeURIComponent(m.body);
  }

  async function handleSave() {
    if (!form.contactId) { setError("Select a contact."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.surveys.create({ id: "sv_" + Math.random().toString(36).slice(2, 9), accountId: contact?.accountId, ...form });
      onClose();
    } catch(e) { setError(e.message || "Failed to log survey."); setSaving(false); }
  }

  return (
    <Dialog title="Send survey" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Log as sent"}
      </button>
    </>}>
      <div className="form-field"><label>Contact *</label>
        <select value={form.contactId} onChange={e => pickContact(e.target.value)}>
          <option value="">— Select contact —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last} · {getAccount(c.accountId)?.name}</option>)}
        </select>
      </div>

      <div className="form-field"><label>Persona</label>
        <select value={persona} onChange={e => { setPersona(e.target.value); setCopiedKey(null); }}>
          <option value="">— Select persona —</option>
          {SURVEY_PERSONAS.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
        </select>
        {form.contactId && !(contact && contact.icp) && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            This contact has no ICP set — pick the persona to send the right form.
          </div>
        )}
      </div>

      {persona && (formUrl ? (
        <div className="form-field">
          <label>Form link to send</label>
          <input value={formUrl} readOnly onFocus={e => e.target.select()} />
          <div className="flex-gap" style={{ marginTop: 6 }}>
            <button type="button" className="btn btn-sm" onClick={() => copy(formUrl, "link")}>{copiedKey === "link" ? "Copied" : "Copy link"}</button>
          </div>
        </div>
      ) : (
        <div className="form-error" style={{ marginBottom: 4 }}>
          No form URL configured for this persona — set it in Admin → Customisation → Survey forms.
        </div>
      ))}

      <div className="form-row">
        <div className="form-field"><label>Survey type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)}>
            <option>ICP Discovery</option><option>Champion validation</option><option>Influencer Mapping</option>
            <option>Pain confirmation</option><option>Win/loss</option><option>NPS</option><option>Pulse</option>
          </select>
        </div>
        <div className="form-field"><label>Channel</label>
          <select value={form.channel} onChange={e => set("channel", e.target.value)}>
            <option>Email</option><option>LinkedIn</option><option>Manual entry</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>Send date</label>
        <input type="date" value={form.sent} onChange={e => set("sent", e.target.value)} />
      </div>

      {message && (
        <div className="form-field">
          <label>
            {form.channel === "LinkedIn" ? "LinkedIn message" : form.channel === "Manual entry" ? "Message" : "Email message"}
          </label>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            Copy this and paste it into {form.channel === "LinkedIn" ? "LinkedIn" : "your email client"} to send to {contact.first}. This won't be sent for you.
          </div>
          {message.subject && (
            <div className="flex-gap" style={{ alignItems: "center", marginBottom: 6 }}>
              <input style={{ flex: 1 }} value={message.subject} readOnly onFocus={e => e.target.select()} />
              <button type="button" className="btn btn-sm" onClick={() => copy(message.subject, "subject")}>{copiedKey === "subject" ? "Copied" : "Copy subject"}</button>
            </div>
          )}
          <textarea value={message.body} readOnly onFocus={e => e.target.select()} style={{ minHeight: 150 }} />
          <div className="flex-gap" style={{ marginTop: 6 }}>
            <button type="button" className="btn btn-sm" onClick={() => copy(message.body, "body")}>{copiedKey === "body" ? "Copied" : "Copy message"}</button>
            {form.channel !== "LinkedIn" && (
              <a className="btn btn-sm" href={mailtoHref()} style={{ textDecoration: "none" }}>Open email draft</a>
            )}
          </div>
        </div>
      )}

      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function SurveysList({ navigate }) {
  const [type, setType] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const rows = D.SURVEYS.filter(s => type === "all" || s.type === type);
  return (
    <>
      <PageHead title="Surveys" sub={`${rows.length} surveys`} actions={<button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ Send survey</button>} />
      <div className="card">
        <FilterToolbar count={rows.length} total={D.SURVEYS.length}>
          <select value={type} onChange={e => setType(e.target.value)}>
            <option value="all">All types</option><option>ICP Discovery</option><option>Champion validation</option><option>Influencer Mapping</option><option>Pain confirmation</option><option>Win/loss</option><option>NPS</option><option>Pulse</option>
          </select>
        </FilterToolbar>
        <table className="tbl tbl-flush">
          <thead><tr><th>Type</th><th>Respondent</th><th>Account</th><th>Status</th><th>Sent</th><th>Completed</th><th>Champion strength</th><th>Analysis</th></tr></thead>
          <tbody>{rows.map(s => {
            const c = getContact(s.contactId) || {}; const a = getAccount(s.accountId) || {};
            return (
              <tr key={s.id} onClick={() => navigate("research/surveys/" + s.id)}>
                <td><Badge tone="blue">{s.type}</Badge></td>
                <td><span className="tbl-link">{c.first} {c.last}</span> <span className="muted" style={{fontSize:11}}>{c.title}</span></td>
                <td>{a.name}</td>
                <td><Badge tone={s.status === "Completed" ? "green" : s.status === "Sent" ? "slate" : "amber"}>{s.status}</Badge></td>
                <td>{fmtDateShort(s.sent)}</td>
                <td>{fmtDateShort(s.completed)}</td>
                <td>{s.championStrength || "—"}</td>
                <td>{s.analysisComplete ? <Badge tone="green">Done</Badge> : <span className="muted">—</span>}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      {showNew && <NewSurveyDialog onClose={() => setShowNew(false)} />}
    </>
  );
}

// Lets a researcher mark a survey / interview's analysis as complete —
// distinct from the data-collection `status`. Optimistic; persists via PUT.
function AnalysisToggle({ record, resource }) {
  const [done, setDone] = useState(!!record.analysisComplete);
  const [saving, setSaving] = useState(false);
  function toggle() {
    const next = !done;
    setDone(next); setSaving(true);
    tpAPI[resource].update(record.id, { analysisComplete: next })
      .catch(() => setDone(!next))
      .finally(() => setSaving(false));
  }
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
      <input type="checkbox" checked={done} onChange={toggle} disabled={saving} />
      <span>Analysis complete</span>
    </label>
  );
}

// Marks the survey / interview itself complete by driving its `status`.
// Unchecking returns it to the open state (Sent / Scheduled). Completing a
// survey that has no `responseId` yet first prompts for the external form's
// response id, so the completed response can be traced back to the form.
function CompletedToggle({ record, resource, openStatus }) {
  const [status, setStatus] = useState(record.status);
  const [saving, setSaving] = useState(false);
  const [askId, setAskId] = useState(false);
  const [responseId, setResponseId] = useState("");
  const done = status === "Completed";

  function persist(nextStatus, extra) {
    const prev = status;
    setStatus(nextStatus); setSaving(true);
    tpAPI[resource].update(record.id, { status: nextStatus, ...extra })
      .catch(() => setStatus(prev))
      .finally(() => setSaving(false));
  }

  function toggle() {
    if (done) { persist(openStatus); return; }   // unchecking never prompts
    if (resource === "surveys" && !record.responseId) { setResponseId(""); setAskId(true); return; }
    persist("Completed");
  }

  function confirmId() {
    const id = responseId.trim();
    if (!id) return;
    setAskId(false);
    persist("Completed", { responseId: id });
  }

  return (
    <>
      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
        <input type="checkbox" checked={done} onChange={toggle} disabled={saving} />
        <span>Completed</span>
      </label>
      {askId && (
        <Dialog title="Survey ID" onClose={() => setAskId(false)} footer={<>
          <button className="btn btn-sm" onClick={() => setAskId(false)}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={confirmId} disabled={!responseId.trim()}>Mark completed</button>
        </>}>
          <div className="form-field">
            <label>Survey ID</label>
            <input autoFocus value={responseId} onChange={e => setResponseId(e.target.value)}
                   placeholder="e.g. survey_dev_1777982059824_a7f9fc05cc0c" />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              The survey ID from the submitted response. It's stored against this survey so the 👁 viewer can load the respondent's answers.
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

// Free-text analysis write-up for a survey / interview — saved inline.
function AnalysisNotesCard({ record, resource }) {
  const [notes, setNotes] = useState(record.analysisNotes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const dirty = notes !== (record.analysisNotes || "");
  async function save() {
    setSaving(true); setError(null);
    try {
      await tpAPI[resource].update(record.id, { analysisNotes: notes });
      setSaved(true);
    } catch (e) { setError(e.message || "Failed to save notes."); }
    finally { setSaving(false); }
  }
  return (
    <div className="card mt-16">
      <div className="card-head">
        <div className="card-title">Analysis notes</div>
        <div className="card-sub">Your write-up of this {resource === "surveys" ? "survey" : "interview"}</div>
      </div>
      <div className="card-body">
        <div className="form-field">
          <textarea value={notes} onChange={e => { setNotes(e.target.value); setSaved(false); }}
                    placeholder="Write your analysis, key findings and next steps…" style={{ minHeight: 130 }} />
        </div>
        {error && <div className="form-error">{error}</div>}
      </div>
      <div className="card-foot flex-between">
        <span className="muted">{dirty ? "Unsaved changes" : saved ? "✓ Saved" : ""}</span>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>{saving ? "Saving…" : "Save notes"}</button>
      </div>
    </div>
  );
}

// ── Survey response viewer ────────────────────────────────────────────
// The respondent's answers live in DynamoDB and are read through the
// server (GET /apicrm/surveys/:id/response, cached to disk). The question
// text + option labels come from the build-time map window.SURVEY_QUESTIONS
// keyed by the response's own surveyType (dev/comp/eng/inf/pm/cto), so the
// raw stored values (q1_role="privacy") render as "Privacy Engineer …".
function deslug(key) {
  const s = String(key).replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : String(key);
}
function isBlankAnswer(v) {
  if (v == null || v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

function SurveyResponseAnswers({ data }) {
  const map = (window.SURVEY_QUESTIONS && window.SURVEY_QUESTIONS[data.surveyType]) || { order: [], fields: {} };
  const fields = map.fields || {};
  const responses = data.responses || {};
  const ordered = (map.order || []).filter(k => k in responses);
  const extra = Object.keys(responses).filter(k => ordered.indexOf(k) === -1);
  // Skip consent flags and blank answers — they aren't survey content.
  const keys = ordered.concat(extra).filter(k => !/^gdpr/.test(k) && !isBlankAnswer(responses[k]));

  if (!keys.length) return <div className="muted">No answers recorded in this response.</div>;

  return (
    <>
      {keys.map(k => {
        const field = fields[k] || {};
        const val = responses[k];
        const question = field.question || deslug(k);

        // Rating group — object of subKey -> rating (1–5).
        if (val && typeof val === "object" && !Array.isArray(val)) {
          return (
            <div key={k} className="kv" style={{ marginBottom: 12 }}>
              <div className="kv-label">{question}</div>
              <div className="kv-value">
                {Object.keys(val).map(sub => (
                  <div key={sub} className="flex-between" style={{ gap: 12 }}>
                    <span>{(field.rows && field.rows[sub]) || deslug(sub)}</span>
                    <span className="numeric">{field.type === "rating" ? val[sub] + "/5" : String(val[sub])}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Multi-select (checkbox) — array of stored values.
        if (Array.isArray(val)) {
          return (
            <div key={k} className="kv" style={{ marginBottom: 12 }}>
              <div className="kv-label">{question}</div>
              <div className="kv-value flex-gap" style={{ flexWrap: "wrap" }}>
                {val.map((v, i) => <Badge key={i} tone="blue">{(field.options && field.options[v]) || v}</Badge>)}
              </div>
            </div>
          );
        }

        // Scalar — map the stored value to its option label when known.
        return (
          <div key={k} className="kv" style={{ marginBottom: 12 }}>
            <div className="kv-label">{question}</div>
            <div className="kv-value">{(field.options && field.options[val]) || String(val)}</div>
          </div>
        );
      })}
    </>
  );
}

function SurveyResponseViewer({ survey, onClose }) {
  const [state, setState] = useState({ status: "loading", data: null, meta: null, error: null });

  function load(refresh) {
    setState(s => ({ ...s, status: "loading", error: null }));
    tpAPI.surveys.getResponse(survey.id, { refresh })
      .then(res => setState({ status: "ready", data: res.data, meta: res, error: null }))
      .catch(err => setState({ status: "error", data: null, meta: null, error: err.message || "Failed to load response." }));
  }
  useEffect(() => { load(false); }, [survey.id]);

  const c = (state.data && state.data.contact) || {};

  return (
    <Dialog title="Survey response" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Close</button>
      <button className="btn btn-sm" onClick={() => load(true)} disabled={state.status === "loading"}>↻ Refresh from source</button>
    </>}>
      {state.status === "loading" && <div className="muted">Loading response…</div>}
      {state.status === "error" && <div className="form-error">{state.error}</div>}
      {state.status === "ready" && state.data && (
        <>
          <div className="kv" style={{ marginBottom: 16 }}>
            {c.name && <><div className="kv-label">Respondent</div><div className="kv-value">{c.name}{c.position ? " · " + c.position : ""}</div></>}
            {c.email && <><div className="kv-label">Email</div><div className="kv-value-mono">{c.email}</div></>}
            {c.organisation && <><div className="kv-label">Organisation</div><div className="kv-value">{c.organisation}</div></>}
            {state.data.submittedAt && <><div className="kv-label">Submitted</div><div className="kv-value">{fmtDate(state.data.submittedAt)}</div></>}
          </div>
          <SurveyResponseAnswers data={state.data} />
          <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            {state.meta && state.meta.source === "cache"
              ? "Loaded from server cache" + (state.meta.cachedAt ? " · " + fmtDate(state.meta.cachedAt) : "") + ". Use Refresh to re-pull from source."
              : "Fetched live from source."}
          </div>
        </>
      )}
    </Dialog>
  );
}

function SurveyDetail({ id, navigate }) {
  const s = D.SURVEYS.find(x => x.id === id);
  const [showResponse, setShowResponse] = useState(false);
  if (!s) return <PageHead title="Not found" />;
  const c = getContact(s.contactId) || {}; const a = getAccount(s.accountId) || {};
  return (
    <>
      <PageHead
        title={`Survey · ${s.type}`}
        sub={<>Respondent <a className="tbl-link" onClick={()=>navigate("sales/contacts/"+c.id)}>{c.first} {c.last}</a> · <a className="tbl-link" onClick={()=>navigate("sales/accounts/"+a.id)}>{a.name}</a></>}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <CompletedToggle record={s} resource="surveys" openStatus="Sent" />
            <AnalysisToggle record={s} resource="surveys" />
            <button className="btn btn-sm" title="View response" onClick={() => setShowResponse(true)}>👁</button>
          </div>
        }
      />
      {showResponse && <SurveyResponseViewer survey={s} onClose={() => setShowResponse(false)} />}
      <div className="detail">
        <div>
          <div className="card">
            <div className="card-head"><div className="card-title">Response</div><div className="card-sub">Form: {s.type} · Status {s.status}</div></div>
            <div className="card-body">
              {s.responses && s.responses.length ? s.responses.map((r,i) => (
                <div key={i} className="kv" style={{ marginBottom: 12 }}>
                  <div className="kv-label">{r.q}</div><div className="kv-value">{r.a}</div>
                </div>
              )) : s.status === "Completed" ? (
                <>
                  <div className="kv">
                    <div className="kv-label">Response ID</div>
                    <div className="kv-value-mono">{s.responseId || "—"}</div>
                    {s.completed && <><div className="kv-label">Completed</div><div className="kv-value">{fmtDate(s.completed)}</div></>}
                  </div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
                    {s.responseId
                      ? "Responses live in the external form — look this ID up there to read the full submission."
                      : "Marked complete, but no response ID was recorded."}
                  </div>
                </>
              ) : <div className="muted">Survey not yet completed.</div>}
              {s.keyQuote && <><div className="section-title">Key quote</div><div style={{ fontStyle: "italic", color: "var(--text)", padding: 12, background: "var(--bg-sunken)", borderRadius: 8 }}>"{s.keyQuote}"</div></>}
              {s.pain && <><div className="section-title">Pain tagged</div><div className="flex-gap" style={{flexWrap:"wrap"}}>{s.pain.map(p=> <Badge key={p} tone="amber">{p}</Badge>)}</div></>}
            </div>
          </div>
          <AnalysisNotesCard record={s} resource="surveys" />
        </div>
        <RightRail record={{...s, createdBy: s.sentBy || "u_sasha"}} />
      </div>
    </>
  );
}

// ── Interview scheduling helpers ──────────────────────────────────────
// The interview `date` column stores either "YYYY-MM-DD" or
// "YYYY-MM-DD HH:MM"; these split / recombine the date and time parts for
// the <input type="date"> + <input type="time"> pair in the dialogs.
function splitDateTime(s) {
  if (!s) return { date: "", time: "" };
  const [d, t] = String(s).split(" ");
  return { date: d || "", time: (t || "").slice(0, 5) };
}
function joinDateTime(d, t) {
  if (!d) return null;
  return t ? `${d} ${t}` : d;
}
// Display a stored schedule slot ("YYYY-MM-DD[ HH:MM]") as a friendly date,
// appending the time when one was recorded.
function fmtScheduleSlot(s) {
  if (!s) return "—";
  const [d, t] = String(s).split(" ");
  return fmtDate(d) + (t ? " · " + t.slice(0, 5) : "");
}

// Per-interview-type framing for the outreach that invites a contact to a
// research conversation. Mirrors the survey message builders above, but the
// ask is a booked conversation (via the persona's scheduling link) rather
// than a self-serve form. Falls back to a generic line for any other type.
const INTERVIEW_MESSAGE_BLURB = {
  "Champion validation": "I'd really value half an hour to understand where your team's software delivery is solid and where it still hurts",
  "ICP Discovery":       "I'm trying to get a clearer picture of how teams like yours actually ship software, and where it slows down",
  "Win/loss":            "I'd love a short call to hear how the evaluation went for you, the good and the bad alike",
  "Pain confirmation":   "I want to be sure I've genuinely understood the pain points in your delivery process, so I'd value a quick conversation",
};

// Respondent-facing subject lines — framed around the recipient's world,
// never the internal interview-type label.
const INTERVIEW_MESSAGE_SUBJECT = {
  "Champion validation": "Half an hour on your team's SDLC?",
  "ICP Discovery":       "How does your team ship software? (quick chat)",
  "Win/loss":            "How did the evaluation go for you?",
  "Pain confirmation":   "The parts of your SDLC that hurt most — a quick chat",
};

// Builds the copy-paste outreach inviting a contact to an interview. Email
// and Manual entry share the longer copy (with a subject); LinkedIn gets a
// shorter, subject-less DM. `bookingUrl` is the persona's scheduling link
// (Calendly / Cal.com etc.) configured in Admin → Research — optional, so a
// rep can also propose a time directly. There is no scheduling backend; the
// rep pastes this into their own email client / LinkedIn to send.
function buildInterviewMessage(channel, { contact, bookingUrl, interviewType, durationMins, senderName }) {
  const first = (contact && contact.first) || "there";
  const blurb = INTERVIEW_MESSAGE_BLURB[interviewType] || "I'd really value a short conversation about how your team works";
  const subject = INTERVIEW_MESSAGE_SUBJECT[interviewType] || "A quick conversation about your software delivery";
  const mins = durationMins || 30;
  const booking = bookingUrl ? "Grab whatever time suits you here: " + bookingUrl : "Just reply with a couple of times that work and I'll send an invite.";
  if (channel === "LinkedIn") {
    return {
      subject: "",
      body: "Hi " + first + ", " + blurb + ". It's about " + mins + " minutes, and I'm happy to share back what we learn. " +
        booking +
        (senderName ? "\n\nReally appreciate it,\n" + senderName : "\n\nReally appreciate it!"),
    };
  }
  return {
    subject: subject,
    body: "Hi " + first + ",\n\n" +
      blurb.charAt(0).toUpperCase() + blurb.slice(1) + ", so I'd love to set up a short call.\n\n" +
      "It takes about " + mins + " minutes, and I'm happy to share back what we learn.\n" +
      booking +
      (senderName ? "\n\nThanks,\n" + senderName : "\n\nThanks!"),
  };
}

function NewInterviewDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const contacts = D.CONTACTS || [];
  const interviewForms = (D.SETTINGS && D.SETTINGS.interviewForms) || {};
  const users = assignableReps();
  const { user } = useAuth();
  const senderName = (user && user.name) || "";
  const [form, setForm] = useState({
    contactId: "", type: "Champion validation", date: today, time: "",
    interviewer: users[0]?.id || "", medium: "Video", status: "Scheduled", duration: 45,
  });
  const [persona, setPersona] = useState("");
  const [channel, setChannel] = useState("Email");
  const [copiedKey, setCopiedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const contact = contacts.find(c => c.id === form.contactId);
  const bookingUrl = persona ? (interviewForms[persona] || "") : "";
  const message = contact
    ? buildInterviewMessage(channel, { contact, bookingUrl, interviewType: form.type, durationMins: form.duration, senderName })
    : null;

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // Picking a contact defaults the persona to that contact's ICP — still
  // overridable for contacts whose ICP has not been set.
  function pickContact(id) {
    set("contactId", id);
    const c = contacts.find(x => x.id === id);
    setPersona((c && c.icp) || "");
    setCopiedKey(null);
  }

  function copy(text, key) {
    if (text && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(k => (k === key ? null : k)), 1500);
    }
  }

  function mailtoHref() {
    const m = buildInterviewMessage("Email", { contact, bookingUrl, interviewType: form.type, durationMins: form.duration, senderName });
    return "mailto:" + (contact && contact.email ? contact.email : "") +
      "?subject=" + encodeURIComponent(m.subject) + "&body=" + encodeURIComponent(m.body);
  }

  async function handleSave() {
    if (!form.contactId) { setError("Select a contact."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.interviews.create({
        id: "i_" + Math.random().toString(36).slice(2, 9),
        accountId: contact?.accountId,
        contactId: form.contactId, type: form.type, status: form.status,
        date: joinDateTime(form.date, form.time), interviewer: form.interviewer,
        medium: form.medium, duration: form.duration,
      });
      onClose();
    } catch(e) { setError(e.message || "Failed to schedule interview."); setSaving(false); }
  }
  return (
    <Dialog title="Schedule interview" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        {saving ? "Scheduling…" : "Schedule"}
      </button>
    </>}>
      <div className="form-field"><label>Contact *</label>
        <select value={form.contactId} onChange={e => pickContact(e.target.value)}>
          <option value="">— Select contact —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last} · {getAccount(c.accountId)?.name}</option>)}
        </select>
      </div>

      <div className="form-field"><label>Persona</label>
        <select value={persona} onChange={e => { setPersona(e.target.value); setCopiedKey(null); }}>
          <option value="">— Select persona —</option>
          {SURVEY_PERSONAS.map(p => <option key={p.slug} value={p.slug}>{p.label}</option>)}
        </select>
        {form.contactId && !(contact && contact.icp) && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            This contact has no ICP set — pick the persona to use the right booking link.
          </div>
        )}
      </div>

      {persona && (bookingUrl ? (
        <div className="form-field">
          <label>Booking link to send</label>
          <input value={bookingUrl} readOnly onFocus={e => e.target.select()} />
          <div className="flex-gap" style={{ marginTop: 6 }}>
            <button type="button" className="btn btn-sm" onClick={() => copy(bookingUrl, "link")}>{copiedKey === "link" ? "Copied" : "Copy link"}</button>
          </div>
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          No booking link configured for this persona (set one in Admin → Research). The invite will ask the contact to reply with times instead.
        </div>
      ))}

      <div className="form-row">
        <div className="form-field"><label>Interview type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)}>
            <option>Champion validation</option><option>ICP Discovery</option>
            <option>Win/loss</option><option>Pain confirmation</option>
          </select>
        </div>
        <div className="form-field"><label>Medium</label>
          <select value={form.medium} onChange={e => set("medium", e.target.value)}>
            <option>Video</option><option>Phone</option><option>In-person</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Date</label>
          <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
        </div>
        <div className="form-field"><label>Time</label>
          <input type="time" value={form.time} onChange={e => set("time", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Duration (min)</label>
          <select value={form.duration} onChange={e => set("duration", Number(e.target.value))}>
            <option value={30}>30 min</option><option value={45}>45 min</option>
            <option value={60}>60 min</option><option value={90}>90 min</option>
          </select>
        </div>
        <div className="form-field"><label>Interviewer</label>
          <select value={form.interviewer} onChange={e => set("interviewer", e.target.value)}>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>

      <div className="form-field"><label>Invite via</label>
        <select value={channel} onChange={e => { setChannel(e.target.value); setCopiedKey(null); }}>
          <option>Email</option><option>LinkedIn</option><option>Manual entry</option>
        </select>
      </div>

      {message && (
        <div className="form-field">
          <label>
            {channel === "LinkedIn" ? "LinkedIn message" : channel === "Manual entry" ? "Message" : "Email message"}
          </label>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            Copy this and paste it into {channel === "LinkedIn" ? "LinkedIn" : "your email client"} to invite {contact.first}. This won't be sent for you.
          </div>
          {message.subject && (
            <div className="flex-gap" style={{ alignItems: "center", marginBottom: 6 }}>
              <input style={{ flex: 1 }} value={message.subject} readOnly onFocus={e => e.target.select()} />
              <button type="button" className="btn btn-sm" onClick={() => copy(message.subject, "subject")}>{copiedKey === "subject" ? "Copied" : "Copy subject"}</button>
            </div>
          )}
          <textarea value={message.body} readOnly onFocus={e => e.target.select()} style={{ minHeight: 150 }} />
          <div className="flex-gap" style={{ marginTop: 6 }}>
            <button type="button" className="btn btn-sm" onClick={() => copy(message.body, "body")}>{copiedKey === "body" ? "Copied" : "Copy message"}</button>
            {channel !== "LinkedIn" && (
              <a className="btn btn-sm" href={mailtoHref()} style={{ textDecoration: "none" }}>Open email draft</a>
            )}
          </div>
        </div>
      )}

      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// Edit an interview's schedule (date / time / medium / duration /
// interviewer) and logistical details (status, linked opp, recording,
// transcript, consent). Notes & coding (summary, key quote, themes, pain)
// stay read-only — they are the qualitative record and are not edited here.
// Changing the date is what drives the append-only schedule log on the
// backend, so the full history of scheduled times is preserved.
function EditInterviewDialog({ interview, onClose }) {
  const init = splitDateTime(interview.date);
  const accountOpps = (D.OPPORTUNITIES || []).filter(o => o.accountId === interview.accountId);
  const users = assignableReps(null, interview.interviewer);
  const [form, setForm] = useState({
    type:          interview.type || "Champion validation",
    status:        interview.status || "Scheduled",
    date:          init.date,
    time:          init.time,
    medium:        interview.medium || "Video",
    duration:      interview.duration || 45,
    interviewer:   interview.interviewer || "",
    opp:           interview.opp || "",
    recording:     interview.recording || "",
    transcript:    !!interview.transcript,
    consentRecord: interview.consentRecord || "Not asked",
    consentQuote:  interview.consentQuote || "Not asked",
    consentDate:   interview.consentDate || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  const rescheduling = joinDateTime(form.date, form.time) !== (interview.date || null);

  async function handleSave() {
    if (!form.date) { setError("A date is required."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.interviews.update(interview.id, {
        type: form.type, status: form.status,
        date: joinDateTime(form.date, form.time),
        medium: form.medium, duration: form.duration, interviewer: form.interviewer,
        opp: form.opp || null, recording: form.recording || null,
        transcript: form.transcript,
        consentRecord: form.consentRecord, consentQuote: form.consentQuote,
        consentDate: form.consentDate || null,
      });
      onClose();
    } catch (e) { setError(e.message || "Failed to save interview."); setSaving(false); }
  }

  return (
    <Dialog title="Edit interview" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
    </>}>
      <div className="form-row">
        <div className="form-field"><label>Interview type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)}>
            <option>Champion validation</option><option>ICP Discovery</option>
            <option>Win/loss</option><option>Pain confirmation</option>
            {/* Tolerate seed/legacy types that aren't in the create list. */}
            {!["Champion validation","ICP Discovery","Win/loss","Pain confirmation"].includes(form.type) && <option>{form.type}</option>}
          </select>
        </div>
        <div className="form-field"><label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)}>
            <option>Scheduled</option><option>Completed</option>
            <option>Cancelled</option><option>No-show</option>
            {!["Scheduled","Completed","Cancelled","No-show"].includes(form.status) && <option>{form.status}</option>}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Date</label>
          <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
        </div>
        <div className="form-field"><label>Time</label>
          <input type="time" value={form.time} onChange={e => set("time", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Medium</label>
          <select value={form.medium} onChange={e => set("medium", e.target.value)}>
            <option>Video</option><option>Phone</option><option>In-person</option>
            {!["Video","Phone","In-person"].includes(form.medium) && <option>{form.medium}</option>}
          </select>
        </div>
        <div className="form-field"><label>Duration (min)</label>
          <select value={form.duration} onChange={e => set("duration", Number(e.target.value))}>
            <option value={30}>30 min</option><option value={45}>45 min</option>
            <option value={60}>60 min</option><option value={90}>90 min</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>Interviewer</label>
        <select value={form.interviewer} onChange={e => set("interviewer", e.target.value)}>
          <option value="">— Unassigned —</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div className="form-field"><label>Linked opportunity</label>
        <select value={form.opp} onChange={e => set("opp", e.target.value)}>
          <option value="">— None —</option>
          {accountOpps.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          {form.opp && !accountOpps.some(o => o.id === form.opp) && <option value={form.opp}>{getOpp(form.opp)?.name || form.opp}</option>}
        </select>
      </div>
      <div className="form-field"><label>Recording link</label>
        <input value={form.recording} onChange={e => set("recording", e.target.value)} placeholder="https://…" />
      </div>
      <div className="form-field">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={form.transcript} onChange={e => set("transcript", e.target.checked)} />
          <span>Transcript available</span>
        </label>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Consent to record</label>
          <select value={form.consentRecord} onChange={e => set("consentRecord", e.target.value)}>
            <option>Not asked</option><option>Yes</option><option>No</option>
          </select>
        </div>
        <div className="form-field"><label>Consent to quote</label>
          <select value={form.consentQuote} onChange={e => set("consentQuote", e.target.value)}>
            <option>Not asked</option><option>Yes</option><option>Anonymous only</option><option>No</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>Consent date</label>
        <input type="date" value={form.consentDate} onChange={e => set("consentDate", e.target.value)} />
      </div>

      {rescheduling && (
        <div className="muted" style={{ fontSize: 12 }}>
          Changing the date adds a new entry to this interview's schedule history — the previous scheduled times are kept.
        </div>
      )}
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// Append-only history of every date/time the interview has been booked for.
// The backend owns this log (interviewsController.js); records that pre-date
// it (e.g. seed data) fall back to showing the current scheduled slot.
function ScheduleHistoryCard({ interview }) {
  const log = Array.isArray(interview.scheduleLog) && interview.scheduleLog.length
    ? interview.scheduleLog
    : (interview.date ? [{ date: interview.date, medium: interview.medium, duration: interview.duration }] : []);
  return (
    <div className="card mt-16">
      <div className="card-head">
        <div className="card-title">Schedule history</div>
        <div className="card-sub">Every time this interview was scheduled — most recent last</div>
      </div>
      {log.length === 0 ? (
        <div className="card-body muted">No schedule recorded yet.</div>
      ) : (
        <table className="tbl tbl-flush">
          <thead><tr><th>Scheduled for</th><th>Medium</th><th>Duration</th><th>Set by</th><th>Logged</th></tr></thead>
          <tbody>{log.map((e, idx) => {
            const isCurrent = idx === log.length - 1;
            return (
              <tr key={idx}>
                <td>{fmtScheduleSlot(e.date)} {isCurrent && <Badge tone="green">Current</Badge>}</td>
                <td>{e.medium || "—"}</td>
                <td>{e.duration ? e.duration + " min" : "—"}</td>
                <td>{e.by || "—"}</td>
                <td>{e.at ? fmtDate(e.at) : "—"}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
  );
}

function InterviewsList({ navigate }) {
  const [showNew, setShowNew] = useState(false);
  return (
    <>
      <PageHead title="Interviews" sub={`${D.INTERVIEWS.length} interviews`} actions={<button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ Schedule interview</button>} />
      <div className="card"><table className="tbl tbl-flush">
        <thead><tr><th>Type</th><th>Respondent</th><th>Account</th><th>Status</th><th>Date</th><th>Researcher</th><th>Key finding</th><th>Analysis</th></tr></thead>
        <tbody>{D.INTERVIEWS.map(i => {
          const c = getContact(i.contactId) || {}; const a = getAccount(i.accountId) || {};
          return (
            <tr key={i.id} onClick={() => navigate("research/interviews/" + i.id)}>
              <td><Badge tone="amber">{i.type}</Badge></td>
              <td><span className="tbl-link">{c.first} {c.last}</span></td>
              <td>{a.name}</td>
              <td><Badge tone={i.status === "Completed" ? "green" : "amber"}>{i.status}</Badge></td>
              <td>{fmtDateShort(i.date)}</td>
              <td><Avatar user={i.interviewer} size={20} /></td>
              <td className="muted" style={{maxWidth:280}}>{i.keyQuote ? `"${i.keyQuote}"` : "—"}</td>
              <td>{i.analysisComplete ? <Badge tone="green">Done</Badge> : <span className="muted">—</span>}</td>
            </tr>
          );
        })}</tbody>
      </table></div>
      {showNew && <NewInterviewDialog onClose={() => setShowNew(false)} />}
    </>
  );
}

function InterviewDetail({ id, navigate }) {
  const i = D.INTERVIEWS.find(x => x.id === id);
  const [editing, setEditing] = useState(false);
  if (!i) return <PageHead title="Not found" />;
  const c = getContact(i.contactId) || {}; const a = getAccount(i.accountId) || {};
  return (
    <>
      <PageHead
        title={`Interview · ${i.type}`}
        sub={<><a className="tbl-link" onClick={()=>navigate("sales/contacts/"+c.id)}>{c.first} {c.last}</a> · <a className="tbl-link" onClick={()=>navigate("sales/accounts/"+a.id)}>{a.name}</a> · {fmtScheduleSlot(i.date)}</>}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <CompletedToggle record={i} resource="interviews" openStatus="Scheduled" />
            <AnalysisToggle record={i} resource="interviews" />
            <button className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>Edit interview</button>
          </div>
        }
      />
      <div className="detail">
        <div>
          <div className="card">
            <div className="card-head"><div className="card-title">Notes & coding</div></div>
            <div className="card-body">
              <div className="section-title">Summary</div>
              <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>{i.summary}</p>
              {i.keyQuote && <><div className="section-title">Key quote</div><div style={{ fontStyle: "italic", padding: 12, background: "var(--bg-sunken)", borderRadius: 8 }}>"{i.keyQuote}"</div></>}
              <div className="section-title">Themes / codes</div>
              <div className="flex-gap" style={{flexWrap:"wrap"}}>{(i.codes||[]).map(t=> <Badge key={t} tone="slate">{t}</Badge>)}</div>
              {i.pain && <><div className="section-title">Pain tagged</div><div className="flex-gap" style={{flexWrap:"wrap"}}>{i.pain.map(p => <Badge key={p} tone="amber">{p}</Badge>)}</div></>}
            </div>
          </div>
          <ScheduleHistoryCard interview={i} />
          <AnalysisNotesCard record={i} resource="interviews" />
        </div>
        <RightRail record={{...i, createdBy: i.interviewer}} />
      </div>
      {editing && <EditInterviewDialog interview={i} onClose={() => setEditing(false)} />}
    </>
  );
}

// Champions / Influencers are per-deal designations now. Both pages walk
// the junction, dedupe by contact, and pick each contact's highest-
// confidence row for their strength display.
function championRowsByContact(matchRoles) {
  const byContact = new Map();
  for (const oc of (D.OPPORTUNITY_CONTACTS || [])) {
    if (!matchRoles.includes(oc.role)) continue;
    const prev = byContact.get(oc.contactId);
    if (!prev || (oc.roleConfidence || 0) > (prev.roleConfidence || 0)) byContact.set(oc.contactId, oc);
  }
  return Array.from(byContact.values());
}

function ChampionsPage({ navigate }) {
  const champRows = championRowsByContact(["Champion"]);
  return (
    <>
      <PageHead title="Champions" sub={`${champRows.length} validated champions across active deals`} />
      <div className="card"><table className="tbl tbl-flush">
        <thead><tr><th>Contact</th><th>Account</th><th>Title</th><th>Strength</th><th>Last validated</th><th>Multithread</th><th className="right">Surveys</th><th className="right">Interviews</th></tr></thead>
        <tbody>{champRows.map(oc => {
          const c = getContact(oc.contactId);
          if (!c) return null;
          const a = getAccount(c.accountId) || {};
          const conf = oc.roleConfidence || 0;
          const surveyCount = (D.SURVEYS || []).filter(s => s.contactId === c.id).length;
          const interviewCount = (D.INTERVIEWS || []).filter(iv => iv.contactId === c.id).length;
          return (
            <tr key={c.id} onClick={() => navigate("sales/contacts/" + c.id)}>
              <td><span className="tbl-link">{c.first} {c.last}</span></td>
              <td>{a.name} <Badge tone="slate">{a.segment}</Badge></td>
              <td className="muted">{c.title}</td>
              <td>{"●".repeat(conf)}<span className="muted">{"●".repeat(Math.max(0, 5 - conf))}</span></td>
              <td>{fmtDateShort(c.lastContact)}</td>
              <td>{a.singleThreaded ? <Badge tone="red">Single-threaded</Badge> : <Badge tone="green">Multi</Badge>}</td>
              <td className="right numeric">{surveyCount}</td>
              <td className="right numeric">{interviewCount}</td>
            </tr>
          );
        })}</tbody>
      </table></div>
    </>
  );
}

function InfluencersPage({ navigate }) {
  const infRows = championRowsByContact(["Influencer", "Technical Evaluator"]);
  return (
    <>
      <PageHead title="Influencers" sub={`${infRows.length} influencers and technical evaluators`} />
      <div className="card"><table className="tbl tbl-flush">
        <thead><tr><th>Contact</th><th>Account</th><th>Role</th><th>Reach</th><th>Last contacted</th></tr></thead>
        <tbody>{infRows.map(oc => {
          const c = getContact(oc.contactId);
          if (!c) return null;
          const a = getAccount(c.accountId) || {};
          const conf = oc.roleConfidence || 0;
          return (
            <tr key={c.id} onClick={() => navigate("sales/contacts/"+c.id)}>
              <td><span className="tbl-link">{c.first} {c.last}</span></td>
              <td>{a.name}</td>
              <td><Badge tone="slate">{oc.role}</Badge></td>
              <td>{"●".repeat(conf)}<span className="muted">{"●".repeat(Math.max(0, 5 - conf))}</span></td>
              <td>{fmtDateShort(c.lastContact)}</td>
            </tr>
          );
        })}</tbody>
      </table></div>
    </>
  );
}

function IcpPage({ navigate }) {
  const tiers = ["Tier 1", "Tier 2", "Tier 3", "Disqualified"];
  return (
    <>
      <PageHead title="ICP fitness" sub="Account fit by tier · refreshed weekly" />
      <div className="grid grid-4 mt-8" style={{ marginTop: 0, marginBottom: 12 }}>
        {tiers.map(t => (
          <Stat key={t} label={t} value={D.ACCOUNTS.filter(a => a.icp === t).length} delta="accounts" />
        ))}
      </div>
      <div className="card">
        <table className="tbl tbl-flush">
          <thead><tr><th>Account</th><th>Tier</th><th>Segment</th><th>Industry</th><th className="right">Score</th><th>Rationale</th></tr></thead>
          <tbody>{D.ACCOUNTS.map(a => (
            <tr key={a.id} onClick={() => navigate("sales/accounts/" + a.id)}>
              <td><span className="tbl-link">{a.name}</span></td>
              <td><Badge tone={a.icp === "Tier 1" ? "amber" : a.icp === "Disqualified" ? "red" : "slate"}>{a.icp}</Badge></td>
              <td>{a.segment}</td>
              <td className="muted">{a.industry}</td>
              <td className="right"><Donut value={a.score} size={28} /></td>
              <td className="muted" style={{ maxWidth: 320, fontSize: 12.5 }}>{a.icpRationale}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </>
  );
}

function NewInsightDialog({ onClose }) {
  const [form, setForm] = useState({ title: "", theme: "Pain", source: "Interviews", confidence: "Low", summary: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.title.trim()) { setError("Title is required."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.insights.create({
        id: "ins_" + Math.random().toString(36).slice(2, 9),
        ...form, linkedAccounts: [], evidence: [], actions: [], evidenceCount: 0, status: "Draft",
      });
      onClose();
    } catch(e) { setError(e.message || "Failed to create insight."); setSaving(false); }
  }
  return (
    <Dialog title="New insight" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Create insight"}
      </button>
    </>}>
      <div className="form-field"><label>Title *</label>
        <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Champions prefer async communication" autoFocus />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Theme</label>
          <select value={form.theme} onChange={e => set("theme", e.target.value)}>
            <option>Pain</option><option>Buying behaviour</option><option>Champion</option>
            <option>ICP</option><option>Competitive</option><option>Product</option>
          </select>
        </div>
        <div className="form-field"><label>Source</label>
          <select value={form.source} onChange={e => set("source", e.target.value)}>
            <option>Interviews</option><option>Surveys</option><option>Win/loss</option>
            <option>Field</option><option>Mixed</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>Confidence</label>
        <select value={form.confidence} onChange={e => set("confidence", e.target.value)}>
          <option>Low</option><option>Medium</option><option>High</option>
        </select>
      </div>
      <div className="form-field"><label>Summary</label>
        <textarea value={form.summary} onChange={e => set("summary", e.target.value)} placeholder="What does this insight tell us and why does it matter?" />
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function InsightsPage({ navigate }) {
  const [showNew, setShowNew] = useState(false);
  return (
    <>
      <PageHead title="Insights" sub={`${D.INSIGHTS.length} synthesised insights · drives messaging, ICP, product`} actions={<button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New insight</button>} />
      <div className="card"><table className="tbl tbl-flush">
        <thead><tr><th>Title</th><th>Theme</th><th>Source</th><th>Confidence</th><th className="right">Evidence</th><th>Linked accounts</th><th>Status</th></tr></thead>
        <tbody>{D.INSIGHTS.map(i => (
          <tr key={i.id} onClick={() => navigate("research/insights/" + i.id)}>
            <td><span className="tbl-link">{i.title}</span></td>
            <td><Badge tone="slate">{i.theme}</Badge></td>
            <td className="muted">{i.source}</td>
            <td><Badge tone={i.confidence === "High" ? "green" : i.confidence === "Medium" ? "amber" : "slate"}>{i.confidence}</Badge></td>
            <td className="right numeric">{i.evidenceCount}</td>
            <td className="muted" style={{fontSize:12}}>{(i.linkedAccounts || []).length} accts</td>
            <td><Badge tone={i.status === "Validated" ? "green" : "amber"}>{i.status}</Badge></td>
          </tr>
        ))}</tbody>
      </table></div>
      {showNew && <NewInsightDialog onClose={() => setShowNew(false)} />}
    </>
  );
}

// Edit dialog for an insight — covers the fields with no other UI path
// (evidence, recommended actions, linked accounts, status) plus the
// fields the create dialog sets. evidenceCount auto-derives from evidence.
function EditInsightDialog({ insight, onClose }) {
  const accounts = D.ACCOUNTS || [];
  const [form, setForm] = useState({
    title:          insight.title || "",
    theme:          insight.theme || "Pain",
    source:         insight.source || "Interviews",
    confidence:     insight.confidence || "Low",
    status:         insight.status || "Draft",
    summary:        insight.summary || "",
    evidence:       [...(insight.evidence || [])],
    actions:        [...(insight.actions || [])],
    // Each linked entry is { account, contact } — contact optional.
    // Tolerates the legacy shape where an entry was a bare account id.
    linkedAccounts: (insight.linkedAccounts || []).map(x =>
      typeof x === "string" ? { account: x, contact: "" }
        : { account: (x && x.account) || "", contact: (x && x.contact) || "" }),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setItem(key, idx, val) { setForm(f => { const a = [...f[key]]; a[idx] = val; return { ...f, [key]: a }; }); }
  function addItem(key) { setForm(f => ({ ...f, [key]: [...f[key], ""] })); }
  function addLinked() { setForm(f => ({ ...f, linkedAccounts: [...f.linkedAccounts, { account: "", contact: "" }] })); }
  function removeItem(key, idx) { setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) })); }

  async function handleSave() {
    if (!form.title.trim()) { setError("Title is required."); return; }
    setSaving(true); setError(null);
    try {
      const evidence       = form.evidence.map(s => s.trim()).filter(Boolean);
      const actions        = form.actions.map(s => s.trim()).filter(Boolean);
      const linkedAccounts = form.linkedAccounts
        .filter(r => r.account)
        .map(r => ({ account: r.account, contact: r.contact || "" }));
      await tpAPI.insights.update(insight.id, {
        title: form.title.trim(), theme: form.theme, source: form.source,
        confidence: form.confidence, status: form.status, summary: form.summary,
        evidence, actions, linkedAccounts, evidenceCount: evidence.length,
      });
      onClose();
    } catch (e) { setError(e.message || "Failed to save insight."); setSaving(false); }
  }

  return (
    <Dialog title="Edit insight" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
    </>}>
      <div className="form-field"><label>Title *</label>
        <input value={form.title} onChange={e => set("title", e.target.value)} autoFocus />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Theme</label>
          <select value={form.theme} onChange={e => set("theme", e.target.value)}>
            <option>Pain</option><option>Buying behaviour</option><option>Champion</option>
            <option>ICP</option><option>Competitive</option><option>Product</option>
          </select>
        </div>
        <div className="form-field"><label>Source</label>
          <select value={form.source} onChange={e => set("source", e.target.value)}>
            <option>Interviews</option><option>Surveys</option><option>Win/loss</option>
            <option>Field</option><option>Mixed</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Confidence</label>
          <select value={form.confidence} onChange={e => set("confidence", e.target.value)}>
            <option>Low</option><option>Medium</option><option>High</option>
          </select>
        </div>
        <div className="form-field"><label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)}>
            <option>Draft</option><option>Emerging</option><option>Validated</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>Summary</label>
        <textarea value={form.summary} onChange={e => set("summary", e.target.value)} placeholder="What does this insight tell us and why does it matter?" />
      </div>

      <div className="form-field"><label>Evidence</label>
        {form.evidence.map((ev, idx) => (
          <div key={idx} className="flex-gap" style={{ marginBottom: 6 }}>
            <input style={{ flex: 1 }} value={ev} onChange={e => setItem("evidence", idx, e.target.value)} placeholder="Evidence touchpoint or finding…" />
            <button className="btn btn-sm" onClick={() => removeItem("evidence", idx)} aria-label="Remove">✕</button>
          </div>
        ))}
        <button className="btn btn-sm" onClick={() => addItem("evidence")}>+ Add evidence</button>
      </div>

      <div className="form-field"><label>Recommended actions</label>
        {form.actions.map((ac, idx) => (
          <div key={idx} className="flex-gap" style={{ marginBottom: 6 }}>
            <input style={{ flex: 1 }} value={ac} onChange={e => setItem("actions", idx, e.target.value)} placeholder="Recommended action…" />
            <button className="btn btn-sm" onClick={() => removeItem("actions", idx)} aria-label="Remove">✕</button>
          </div>
        ))}
        <button className="btn btn-sm" onClick={() => addItem("actions")}>+ Add action</button>
      </div>

      <div className="form-field"><label>Linked accounts</label>
        {form.linkedAccounts.map((row, idx) => {
          const acctContacts = (D.CONTACTS || []).filter(c => c.accountId === row.account);
          return (
            <div key={idx} className="flex-gap" style={{ marginBottom: 6 }}>
              <select style={{ flex: 1 }} value={row.account}
                      onChange={e => setItem("linkedAccounts", idx, { account: e.target.value, contact: "" })}>
                <option value="">— Account —</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <select style={{ flex: 1 }} value={row.contact} disabled={!row.account}
                      onChange={e => setItem("linkedAccounts", idx, { account: row.account, contact: e.target.value })}>
                <option value="">— Contact (optional) —</option>
                {acctContacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last}</option>)}
              </select>
              <button className="btn btn-sm" onClick={() => removeItem("linkedAccounts", idx)} aria-label="Remove">✕</button>
            </div>
          );
        })}
        <button className="btn btn-sm" onClick={addLinked}>+ Add account</button>
      </div>

      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function InsightDetail({ id, navigate }) {
  const i = D.INSIGHTS.find(x => x.id === id);
  const [editing, setEditing] = useState(false);
  if (!i) return <PageHead title="Not found" />;
  return (
    <>
      <PageHead
        title={i.title}
        sub={<><Badge tone="slate">{i.theme}</Badge> · {i.source} · <Badge tone={i.confidence==="High"?"green":"amber"}>{i.confidence}</Badge></>}
        actions={<button className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>Edit insight</button>}
      />
      <div className="detail">
        <div>
          <div className="card">
            <div className="card-head"><div className="card-title">Synthesis</div></div>
            <div className="card-body">
              <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>{i.summary}</p>
              <div className="section-title">Evidence</div>
              <ul style={{ paddingLeft: 18, color: "var(--text-2)", fontSize: 13, lineHeight: 1.75 }}>
                {(i.evidence || []).map((e,k) => <li key={k}>{e}</li>)}
              </ul>
              <div className="section-title">Linked accounts</div>
              <div className="flex-gap" style={{flexWrap:"wrap"}}>{(i.linkedAccounts || []).map((item, idx) => {
                const link = typeof item === "string" ? { account: item } : (item || {});
                const a = getAccount(link.account); if (!a) return null;
                const c = link.contact ? getContact(link.contact) : null;
                return <Badge key={idx} tone="slate" onClick={() => navigate("sales/accounts/" + link.account)}>{a.name}{c ? `: ${c.first} ${c.last}` : ""}</Badge>;
              })}</div>
              <div className="section-title">Recommended actions</div>
              <ul style={{ paddingLeft: 18, color: "var(--text-2)", fontSize: 13, lineHeight: 1.75 }}>
                {(i.actions || []).map((a,k) => <li key={k}>{a}</li>)}
              </ul>
            </div>
          </div>
        </div>
        <RightRail record={{...i, createdBy: i.author}} />
      </div>
      {editing && <EditInsightDialog insight={i} onClose={() => setEditing(false)} />}
    </>
  );
}

// =====================================================================
// Referrals & rewards — track champions / influencers who refer accounts
// =====================================================================
function LogReferralDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const contacts = D.CONTACTS || [];
  const accounts = D.ACCOUNTS || [];
  const [form, setForm] = useState({ referrerContactId: "", accountId: "", referredOn: today, status: "Referred", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.referrerContactId) { setError("Select the referrer."); return; }
    if (!form.accountId)         { setError("Select the referred account."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.referrals.create({ id: "ref_" + Math.random().toString(36).slice(2, 9), ...form });
      onClose();
    } catch (e) { setError(e.message || "Failed to log referral."); setSaving(false); }
  }
  return (
    <Dialog title="Log referral" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Log referral"}</button>
    </>}>
      <div className="form-field"><label>Referrer — champion / influencer *</label>
        <select value={form.referrerContactId} onChange={e => set("referrerContactId", e.target.value)}>
          <option value="">— Select contact —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last} · {getAccount(c.accountId)?.name || ""}</option>)}
        </select>
      </div>
      <div className="form-field"><label>Referred account *</label>
        <select value={form.accountId} onChange={e => set("accountId", e.target.value)}>
          <option value="">— Select account —</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Referred on</label>
          <input type="date" value={form.referredOn} onChange={e => set("referredOn", e.target.value)} />
        </div>
        <div className="form-field"><label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)}>
            <option>Referred</option><option>In pipeline</option><option>Won</option><option>Lost</option>
          </select>
        </div>
      </div>
      <div className="form-field"><label>Notes</label>
        <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Context on the referral…" />
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function LogRewardDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const contacts = D.CONTACTS || [];
  const [form, setForm] = useState({ referrerContactId: "", type: "Gift card", description: "", value: "", status: "Planned", date: today });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.referrerContactId) { setError("Select the recipient."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.rewards.create({
        id: "rwd_" + Math.random().toString(36).slice(2, 9),
        ...form, value: form.value ? Number(form.value) : null,
      });
      onClose();
    } catch (e) { setError(e.message || "Failed to log reward."); setSaving(false); }
  }
  return (
    <Dialog title="Log reward" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Log reward"}</button>
    </>}>
      <div className="form-field"><label>Recipient — champion / influencer *</label>
        <select value={form.referrerContactId} onChange={e => set("referrerContactId", e.target.value)}>
          <option value="">— Select contact —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.first} {c.last} · {getAccount(c.accountId)?.name || ""}</option>)}
        </select>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Type</label>
          <select value={form.type} onChange={e => set("type", e.target.value)}>
            <option>Gift card</option><option>Swag</option><option>Discount</option>
            <option>Event invite</option><option>Cash bonus</option><option>Other</option>
          </select>
        </div>
        <div className="form-field"><label>Value</label>
          <input type="number" value={form.value} onChange={e => set("value", e.target.value)} placeholder="100" />
        </div>
      </div>
      <div className="form-field"><label>Description</label>
        <input value={form.description} onChange={e => set("description", e.target.value)} placeholder="e.g. $100 gift card for the Lumenly intro" />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)}>
            <option>Planned</option><option>Approved</option><option>Sent</option>
          </select>
        </div>
        <div className="form-field"><label>Date</label>
          <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function ReferralsPage({ navigate }) {
  const [dialog, setDialog] = useState(null);
  const referrals = D.REFERRALS || [];
  const rewards = D.REWARDS || [];
  const advocateIds = [...new Set([
    ...referrals.map(r => r.referrerContactId),
    ...rewards.map(r => r.referrerContactId),
  ].filter(Boolean))];

  return (
    <>
      <PageHead
        title="Referrals"
        sub={`${advocateIds.length} advocates · ${referrals.length} referrals · ${rewards.length} rewards`}
        actions={<>
          <button className="btn btn-sm" onClick={() => setDialog("reward")}>+ Log reward</button>
          <button className="btn btn-primary btn-sm" onClick={() => setDialog("referral")}>+ Log referral</button>
        </>}
      />
      {advocateIds.length === 0 ? (
        <div className="card"><div className="card-body muted">No referrals logged yet. Use “Log referral” to record an account a champion or influencer referred — one advocate can refer many accounts.</div></div>
      ) : advocateIds.map(cid => {
        const c = getContact(cid) || {};
        const home = getAccount(c.accountId) || {};
        const myRefs = referrals.filter(r => r.referrerContactId === cid);
        const myRewards = rewards.filter(r => r.referrerContactId === cid);
        return (
          <div key={cid} className="card" style={{ marginBottom: 12 }}>
            <div className="card-head">
              <div className="card-title">
                <span className="tbl-link" onClick={() => c.id && navigate("sales/contacts/" + c.id)}>{c.first} {c.last}</span>
                {(() => {
                  // Role badge shows this advocate's role on their most
                  // recent open deal (option B). Per-deal model, so an
                  // advocate without an active deal renders no badge.
                  const opp = c.id ? getActiveOppForContact(c.id) : null;
                  const oc = opp ? getOppContactRole(opp.id, c.id) : null;
                  return oc && oc.role ? <> · <Badge tone="amber">{oc.role}</Badge></> : null;
                })()}
              </div>
              <div className="card-sub">{home.name || "—"} · {myRefs.length} referral{myRefs.length === 1 ? "" : "s"} · {myRewards.length} reward{myRewards.length === 1 ? "" : "s"}</div>
            </div>
            <div className="card-body">
              <div className="section-title">Referred accounts</div>
              {myRefs.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>None</div> : (
                <table className="tbl tbl-flush">
                  <thead><tr><th>Account</th><th>Status</th><th>Referred on</th><th>Notes</th></tr></thead>
                  <tbody>{myRefs.map(r => {
                    const a = getAccount(r.accountId) || {};
                    return (
                      <tr key={r.id} onClick={() => r.accountId && navigate("sales/accounts/" + r.accountId)}>
                        <td><span className="tbl-link">{a.name || r.accountId}</span></td>
                        <td><Badge tone={r.status === "Won" ? "green" : r.status === "Lost" ? "red" : "slate"}>{r.status || "—"}</Badge></td>
                        <td>{fmtDateShort(r.referredOn)}</td>
                        <td className="muted">{r.notes || "—"}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              )}
              <div className="section-title">Rewards</div>
              {myRewards.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>None given yet</div> : (
                <table className="tbl tbl-flush">
                  <thead><tr><th>Type</th><th>Description</th><th className="right">Value</th><th>Status</th><th>Date</th></tr></thead>
                  <tbody>{myRewards.map(r => (
                    <tr key={r.id} style={{ cursor: "default" }}>
                      <td>{r.type}</td>
                      <td className="muted">{r.description || "—"}</td>
                      <td className="right numeric">{r.value ? fmtMoney(r.value) : "—"}</td>
                      <td><Badge tone={r.status === "Sent" ? "green" : r.status === "Approved" ? "blue" : "slate"}>{r.status || "—"}</Badge></td>
                      <td>{fmtDateShort(r.date)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        );
      })}
      {dialog === "referral" && <LogReferralDialog onClose={() => setDialog(null)} />}
      {dialog === "reward" && <LogRewardDialog onClose={() => setDialog(null)} />}
    </>
  );
}

window.ReferralsPage = ReferralsPage;
window.ResearchDashboard = ResearchDashboard;
window.SurveysList = SurveysList; window.SurveyDetail = SurveyDetail;
window.InterviewsList = InterviewsList; window.InterviewDetail = InterviewDetail;
window.ChampionsPage = ChampionsPage; window.InfluencersPage = InfluencersPage;
window.IcpPage = IcpPage; window.InsightsPage = InsightsPage; window.InsightDetail = InsightDetail;
